import React from "react";

/**
 * Application-wide error boundary.
 *
 * Catches render-time exceptions anywhere below it and renders a friendly
 * fallback instead of the React white-screen. Children render normally on
 * the happy path; only crashes hit the fallback.
 *
 * We deliberately do NOT auto-reload - silent recovery hides bugs from the
 * user (and from us). The user explicitly chooses "Refresh" so they keep
 * agency, and we log to console + window so platform monitoring can pick
 * it up.
 */
export default class ErrorBoundary extends React.Component {
  state = { error: null, info: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the JS console (and any wrapped error reporter the platform
    // might add later, e.g. window.onerror hooks).
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  handleGoHome = () => {
    try { window.location.href = "/"; } catch { /* ignore */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = (this.state.error && this.state.error.message) || String(this.state.error);
    return (
      <div data-testid="app-error-boundary"
           className="min-h-screen bg-[#F6F9FE] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-[#4B5773]">Something went wrong</div>
          <h1 className="font-display text-3xl text-[#0B1A36] mt-2">
            We hit an unexpected snag.
          </h1>
          <p className="text-sm text-[#4B5773] mt-3">
            The page failed to render. Refreshing usually fixes this. If it keeps happening,
            please get in touch at <a href="mailto:hello@nextrota.com" className="text-[#2563EB] underline">hello@nextrota.com</a>.
          </p>
          <details className="mt-4 text-xs text-[#4B5773]">
            <summary className="cursor-pointer">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-all bg-slate-50 rounded p-2 border border-slate-100">{msg}</pre>
          </details>
          <div className="mt-6 flex gap-2">
            <button data-testid="app-error-reload" onClick={this.handleReload}
                    className="px-4 py-2 rounded-md bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8]">
              Refresh
            </button>
            <button data-testid="app-error-home" onClick={this.handleGoHome}
                    className="px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-[#0B1A36] hover:bg-slate-50">
              Go to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
